import React from "react";
// Common Components
import {
  DynamicDropdown,
  Button,
  TextInput,
  DynamicMultiSelectDropdown,
  StaticDropdown,
  CheckBox,
} from "src/Components";
// Request
import { getStepInstruments } from "src/Requests/stepRequests";
import { getBaseInstruments } from "src/Requests/Order/tradeTableRequests";
import { getBaseTypeDropdown, getPairType } from "src/Requests/coveredCall";
// Types
import { IEnterPairPositionSearchParams } from "src/Interfaces/coveredCall";
import { ExchangeType } from "src/Interfaces/connections";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";

const exchangeTypeOption: DropdownItem[] = [
  { id: ExchangeType.StockExchange, name: "اوراق بهادار" },
  { id: ExchangeType.CommodityExchange, name: "کالا" },
];

interface IProps {
  searchParamChangeHandler: (name: string, value: any) => void;
  searchParam: IEnterPairPositionSearchParams;
  onSubmit: () => void;
  useFixedBasePrices?: boolean;
}

const PairPositionSearchParamUI: React.FC<IProps> = ({
  searchParam,
  searchParamChangeHandler,
  onSubmit,
  useFixedBasePrices = false,
}) => {
  return (
    <div>
      <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-[350px] !min-w-[350px] mb-2">
        <div className="flex gap-2 w-1/2">
          <TextInput
            name="dtmBoundry"
            label="حد DTM"
            extraLabel
            onChange={searchParamChangeHandler}
            value={searchParam?.dtmBoundry?.toString() || ""}
            kind="number"
          />
        </div>
        <div className="flex gap-2">
          <TextInput
            name="minDeltaInSubMode"
            label="حداقل دلتا در بیشتر از حد"
            extraLabel
            onChange={searchParamChangeHandler}
            value={searchParam?.minDeltaInSubMode?.toString() || ""}
            kind="negativeDecimalNumbers"
          />
          <TextInput
            name="minITMPercentInOverMode"
            label="حداقل درصد ITM در کمتر از حد"
            extraLabel
            onChange={searchParamChangeHandler}
            value={searchParam?.minITMPercentInOverMode?.toString() || ""}
            kind="negativeDecimalNumbers"
          />
        </div>
      </div>
      <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-[350px] !min-w-[350px]">
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "baseInstrument",
                placeholder: "نماد پایه",
                extraLabel: true,
              }}
              getData={() => getBaseInstruments()}
              onChange={searchParamChangeHandler}
              value={searchParam?.baseInstrument}
            />
          </div>
          <div className="w-full">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              onChange={searchParamChangeHandler}
              options={exchangeTypeOption}
              value={searchParam?.exchangeType?.toString() || ""}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minYesterdayValue"
              label="حداقل ارزش معاملاتی روز قبل"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minYesterdayValue?.toString() || ""}
              kind="price"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="minLastTradeValue"
              label="حداقل حجم معاملات روز قبل"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minLastTradeValue?.toString() || ""}
              kind="price"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            {useFixedBasePrices ? (
              <div className="flex flex-col gap-1">
                <div className="text-[10px]">نوع جفت</div>
                <div className="rounded border border-gray-300 bg-gray-50 px-3 py-2 text-xs">
                  کاورد کال
                </div>
              </div>
            ) : (
              <DynamicMultiSelectDropdown
                dropdown={{
                  name: "pairType",
                  placeholder: "نوع جفت",
                  extraLabel: true,
                }}
                getData={getPairType}
                onChange={searchParamChangeHandler}
                value={searchParam?.pairType}
              />
            )}
          </div>
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "baseType",
                placeholder: "نوع پایه",
                extraLabel: true,
              }}
              getData={getBaseTypeDropdown}
              onChange={searchParamChangeHandler}
              value={searchParam?.baseType}
            />
          </div>
        </div>
        {!useFixedBasePrices ? (
          <div className="flex flex-row justify-start items-start gap-3 w-full">
            <div className="w-full">
              <CheckBox
                checked={searchParam?.ignoreQueue || false}
                name="ignoreQueue"
                label="نمایش نماد پایه در صف خرید"
                onChange={searchParamChangeHandler}
              />
            </div>
          </div>
        ) : null}

        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "buySideInstrument",
                placeholder: "نماد سمت خرید",
                extraLabel: true,
              }}
              getData={getStepInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.buySideInstrument?.toString() || ""}
            />
          </div>
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "sellSideInstrument",
                placeholder: "نماد سمت فروش",
                extraLabel: true,
              }}
              getData={getStepInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.sellSideInstrument?.toString() || ""}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minDailyProfit"
              label="حداقل درصد بازده روزانه"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minDailyProfit?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxDailyOffestProfit"
              label="حداکثر درصد بازده آفست روزانه"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.maxDailyOffestProfit?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        {!useFixedBasePrices ? (
          <>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minCallITMPercentSide1"
                  label="حداقل درصد ITM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minCallITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxCallITMPercentSide1"
                  label="حداکثر درصد ITM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxCallITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
            </div>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minPutITMPercentSide1"
                  label="حداقل درصد ITM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minPutITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxPutITMPercentSide1"
                  label="حداکثر درصد ITM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxPutITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
            </div>
          </>
        ) : null}
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minCallITMPercentSide2"
              label="حداقل درصد ITM کال سمت فروش"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minCallITMPercentSide2?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="positionBreakEvenDiffPercent1"
              label="حداقل % فاصله تا نقطه سر به سر"
              extraLabel
              onChange={searchParamChangeHandler}
              value={
                searchParam?.positionBreakEvenDiffPercent1?.toString() || ""
              }
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        {!useFixedBasePrices ? (
          <div className="flex flex-row justify-start items-start gap-3 w-full">
            <div className="w-full">
              <TextInput
                name="minBuysideRemainingDay"
                label="حداقل DTM خرید"
                extraLabel
                onChange={searchParamChangeHandler}
                value={searchParam?.minBuysideRemainingDay?.toString() || ""}
                kind="number"
              />
            </div>
            <div className="w-full">
              <TextInput
                name="maxBuysideRemainingDay"
                label="حداکثر DTM خرید"
                extraLabel
                onChange={searchParamChangeHandler}
                value={searchParam?.maxBuysideRemainingDay?.toString() || ""}
                kind="number"
              />
            </div>
          </div>
        ) : null}
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              label="حداقل DTM فروش"
              onChange={searchParamChangeHandler}
              name="minSellsideRemainingDay"
              value={searchParam.minSellsideRemainingDay?.toString()}
              extraLabel
              kind="number"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxSellsideRemainingDay"
              label="حداکثر DTM فروش"
              extraLabel
              kind="number"
              onChange={searchParamChangeHandler}
              value={searchParam?.maxSellsideRemainingDay?.toString()}
            />
          </div>
        </div>
        {!useFixedBasePrices ? (
          <>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minCallDTMSide1"
                  label="حداقل DTM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minCallDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxCallDTMSide1"
                  label="حداکثر DTM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxCallDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
            </div>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minPutDTMSide1"
                  label="حداقل DTM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minPutDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxPutDTMSide1"
                  label="حداکثر DTM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxPutDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
            </div>
          </>
        ) : null}
        <Button onClick={onSubmit} text="جستجو" variant="contained" />
      </div>
    </div>
  );
};

export default PairPositionSearchParamUI;
